'use client'

import { useEffect, useState } from 'react'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import {
  clickEvent,
  selfServiceViewScreenEvent,
  BuyerRefundInformationModal,
  ReturnShippingOption,
  EscrowRefundEstimateModel,
} from '@marketplace-web/complaints/complaints-data'

import BundleRefundsBuyerProtectionFeeModal from '../BundleRefundsBuyerProtectionFeeModal'
import ComplaintRefundsOrderSavingsModal from '../ComplaintRefundsOrderSavingsModal'
import BuyerRefundSummaryModal from './BuyerRefundSummaryModal'
import BuyerCurrencyConversionModal from '../BuyerCurrencyConversionModal'

type Props = {
  note?: string
  transactionId?: number | null
  refundEstimate?: EscrowRefundEstimateModel | null
  isBundleComplaint?: boolean
  isSellerBusinessUser?: boolean
  isOpen: boolean
  isLoading?: boolean
  selectedShippingOption?: ReturnShippingOption
  isOpenedInDialog?: boolean
  onClose: () => void
}

const BuyerRefundInformation = ({
  note,
  refundEstimate,
  transactionId,
  isOpen,
  isLoading,
  isBundleComplaint,
  isSellerBusinessUser,
  selectedShippingOption,
  onClose,
  isOpenedInDialog = false,
}: Props) => {
  const { track } = useTracking()

  const [activeModal, setActiveModal] = useState<BuyerRefundInformationModal>(
    BuyerRefundInformationModal.EstimatedRefund,
  )

  useEffect(() => {
    if (!isOpen || !transactionId) return

    track(selfServiceViewScreenEvent({ screen: 'estimated_refund_information', transactionId }))
  }, [track, isOpen, transactionId])

  const trackItemDiscountsInfoClick = () => {
    if (!transactionId) return

    track(
      clickEvent({
        screen: 'estimated_refund_information',
        target: 'item_discounts_refund_information',
        targetDetails: JSON.stringify({
          transaction_id: transactionId,
        }),
      }),
    )
  }

  const trackBuyerProtectionFeeInfoClick = () => {
    if (!transactionId) return

    track(
      clickEvent({
        screen: 'estimated_refund_information',
        target: 'bpf_refund_information',
        targetDetails: JSON.stringify({
          transaction_id: transactionId,
        }),
      }),
    )
  }

  const openEstimatedRefundModal = () => {
    setActiveModal(BuyerRefundInformationModal.EstimatedRefund)
  }

  const openBuyerProtectionFeeModal = () => {
    trackBuyerProtectionFeeInfoClick()
    setActiveModal(BuyerRefundInformationModal.BuyerProtectionFee)
  }

  const openItemDiscountModal = () => {
    trackItemDiscountsInfoClick()
    setActiveModal(BuyerRefundInformationModal.ItemDiscounts)
  }

  const openCurrencyConversionModal = () => {
    setActiveModal(BuyerRefundInformationModal.CurrencyConversion)
  }

  if (!isOpen) return null

  return (
    <>
      <BuyerRefundSummaryModal
        note={note}
        selectedShippingOption={selectedShippingOption}
        isBundleComplaint={isBundleComplaint}
        isOpen={activeModal === BuyerRefundInformationModal.EstimatedRefund}
        onClose={onClose}
        isLoading={isLoading}
        refundEstimate={refundEstimate}
        isSellerBusinessUser={isSellerBusinessUser}
        onBuyerProtectionFeeInfoClick={openBuyerProtectionFeeModal}
        onCurrencyConversionInfoClick={openCurrencyConversionModal}
        onItemDiscountsInfoClick={openItemDiscountModal}
        isOpenedInDialog={isOpenedInDialog}
      />
      <ComplaintRefundsOrderSavingsModal
        isBundleComplaint={isBundleComplaint}
        isOpen={activeModal === BuyerRefundInformationModal.ItemDiscounts}
        onClose={openEstimatedRefundModal}
        isOpenedInDialog={isOpenedInDialog}
      />
      <BundleRefundsBuyerProtectionFeeModal
        isOpen={activeModal === BuyerRefundInformationModal.BuyerProtectionFee}
        onClose={openEstimatedRefundModal}
        isSellerBusinessUser={isSellerBusinessUser}
        isOpenedInDialog={isOpenedInDialog}
      />
      <BuyerCurrencyConversionModal
        refundEstimate={refundEstimate}
        isOpen={activeModal === BuyerRefundInformationModal.CurrencyConversion}
        onClose={openEstimatedRefundModal}
        isOpenedInDialog={isOpenedInDialog}
      />
    </>
  )
}

export default BuyerRefundInformation
